"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var formatter_1 = require("../utils/formatter");
var get_1 = require("../utils/get");
var typeChecks_1 = require("../utils/typeChecks");
var ConditionalHelper_1 = require("../helpers/ConditionalHelper");
var Transform_1 = require("./transform/Transform");
var Query_1 = require("./Query");
var MAX_CALL_STACK_LEVEL = 20;
var BaseQuery = /** @class */ (function () {
    function BaseQuery(options, translationHelper) {
        this.options = options;
        this.translationHelper = translationHelper;
        this.sorters = {
            date: function (a, b) {
                var aDate = new Date(a);
                var bDate = new Date(b);
                return Number(aDate) - Number(bDate);
            },
            number: function (a, b) {
                return a - b;
            },
            string: function (a, b, culture) {
                var aString = a.toString().toUpperCase();
                var bString = b.toString().toUpperCase();
                return aString.localeCompare(bString, culture);
            },
        };
    }
    BaseQuery.prototype.execute = function (executionOptions, callStackLevel) {
        if (callStackLevel === void 0) { callStackLevel = 1; }
        if (callStackLevel > MAX_CALL_STACK_LEVEL) {
            console.warn('BaseQuery MAX_CALL_STACK_LEVEL exceeded');
            return;
        }
        var culture = executionOptions.culture, currency = executionOptions.currency, defaultPlaceholder = executionOptions.placeholder, translate = executionOptions.translate;
        var _a = this.options, format = _a.format, _b = _a.placeholder, placeholder = _b === void 0 ? defaultPlaceholder : _b, transforms = _a.transforms, _c = _a.subQueries, subQueries = _c === void 0 ? [] : _c;
        // Validate that the performance values are allowed and this is a performance value
        if (!this.passesPerformanceCheck(executionOptions)) {
            return placeholder;
        }
        var rawValue = this.getRawValue(executionOptions, callStackLevel);
        if (typeChecks_1.isNullOrUndefined(rawValue)) {
            rawValue = placeholder;
        }
        if (transforms) {
            for (var _i = 0, transforms_1 = transforms; _i < transforms_1.length; _i++) {
                var transformOptions = transforms_1[_i];
                var transform = new Transform_1.Transform(transformOptions, this.translationHelper);
                rawValue = transform.execute(rawValue, this.options, executionOptions);
            }
        }
        if (format) {
            var type = format.type, transforms_2 = format.transforms;
            var workingCurrency = currency;
            if (subQueries) {
                var currencyQueryOptions = subQueries.find(function (subQuery) { return subQuery.queryId === 'currency'; });
                if (currencyQueryOptions) {
                    var currencyQuery = new Query_1.Query(currencyQueryOptions);
                    workingCurrency = currencyQuery.execute(executionOptions);
                }
            }
            rawValue = formatter_1.default(type, culture, workingCurrency, format.options, rawValue, transforms_2, translate);
        }
        if (translate && typeof rawValue === 'string') {
            return translate(rawValue);
        }
        return rawValue;
    };
    BaseQuery.prototype.passesPerformanceCheck = function (executionOptions) {
        var context = Query_1.Query.getContext(executionOptions, this.options);
        var _a = (context || {}).isPerformanceAllowed, isPerformanceAllowed = _a === void 0 ? false : _a;
        var checkPerformanceAllowed = this.options.checkPerformanceAllowed;
        return (!checkPerformanceAllowed || isPerformanceAllowed);
    };
    BaseQuery.prototype.getIndex = function (targetIndex, collection) {
        var response = -1;
        if (collection && collection.length > 0 && Math.abs(targetIndex) < collection.length) {
            if (targetIndex >= 0) {
                response = targetIndex;
            }
            else {
                // If the target index is negative take from the back of the array
                response = collection.length + targetIndex;
            }
        }
        return response;
    };
    BaseQuery.prototype.applySort = function (collection, sort, culture) {
        var _this = this;
        if (sort) {
            var sortFunction_1 = this.sorters[sort.type];
            if (sortFunction_1) {
                collection.sort(function (a, b) {
                    var aValue = get_1.get(a, sort.key);
                    var bValue = get_1.get(b, sort.key);
                    return sortFunction_1(aValue, bValue, culture);
                });
                if (sort.direction === 'DESC') {
                    collection.reverse();
                }
            }
            if (sort.order && sort.order.length > 0) {
                var order_1 = sort.translateOrderValues && this.translationHelper && this.translationHelper.translate ?
                    sort.order.map(function (value) { return _this.translationHelper.translate(value); }) :
                    sort.order;
                collection = collection
                    .map(function (value) {
                    var orderIndex = order_1.indexOf(value[sort.key]);
                    value.__order =
                        orderIndex > -1 ? orderIndex : order_1.length;
                    return value;
                })
                    .sort(function (a, b) { return _this.sorters.number(a.__order, b.__order); })
                    .map(function (value) {
                    delete value.__order;
                    return value;
                });
            }
        }
        return collection;
    };
    BaseQuery.prototype.getEntity = function (executionOptions) {
        var conditionalHelper = new ConditionalHelper_1.ConditionalHelper({ conditions: [] }, this.translationHelper);
        var context = Query_1.Query.getContext(executionOptions, this.options);
        if (context) {
            var entityByType = context.entityByType;
            var _a = this.options, entityType = _a.entityType, entityConditional = _a.entityConditional;
            var entity = entityByType && entityType && entityByType[entityType];
            if (entity && (!entityConditional || conditionalHelper.matches(entity, entityConditional))) {
                return entity;
            }
        }
        return;
    };
    BaseQuery.prototype.getFundManager = function (executionOptions) {
        var fundManagerContext = executionOptions.fundManagerContext;
        if (fundManagerContext) {
            return fundManagerContext;
        }
        return;
    };
    BaseQuery.prototype.getStatistic = function (executionOptions) {
        var statisticContext = executionOptions.statisticContext;
        if (statisticContext) {
            return statisticContext;
        }
        return;
    };
    BaseQuery.prototype.getSubCollection = function (executionOptions) {
        var queryOptionsType = this.options.queryOptionsType;
        var entity = this.getEntity(executionOptions);
        if (entity && entity[queryOptionsType]) {
            return entity[queryOptionsType];
        }
        return [];
    };
    BaseQuery.prototype.getCollectionItem = function (executionOptions) {
        var conditionalHelper = new ConditionalHelper_1.ConditionalHelper({ conditions: [] }, this.translationHelper);
        var _a = this.options, code = _a.code, _b = _a.collectionItemConditional, collectionItemConditional = _b === void 0 ? { conditions: [] } : _b;
        if (code && !collectionItemConditional.conditions.find(function (condition) { return condition.field === 'code'; })) {
            collectionItemConditional.conditions.push({
                field: 'code',
                operator: '=',
                value: code,
            });
        }
        var collectionItem = this.getSubCollection(executionOptions)
            .find(function (item) { return conditionalHelper.matches(item, collectionItemConditional); });
        return collectionItem;
    };
    BaseQuery.prototype.getItem = function (executionOptions, collection) {
        var culture = executionOptions.culture;
        var _a = this.options, sort = _a.sort, _b = _a.startIndex, startIndex = _b === void 0 ? 0 : _b, endIndex = _a.endIndex, index = _a.index;
        var sortedCollection = this.applySort(collection, sort, culture);
        sortedCollection = sortedCollection.slice(startIndex, endIndex);
        if (sortedCollection && sortedCollection.length > 0) {
            if (typeof index === 'number') {
                var targetIndex = this.getIndex(index, sortedCollection);
                if (targetIndex > -1) {
                    var item = sortedCollection[targetIndex];
                    return item;
                }
            }
            else {
                return sortedCollection;
            }
        }
        return;
    };
    BaseQuery.prototype.getRawValueFromSubCollection = function (executionOptions) {
        var conditionalHelper = new ConditionalHelper_1.ConditionalHelper({ conditions: [] }, this.translationHelper);
        var _a = this.options, key = _a.key, collectionItemValueConditional = _a.collectionItemValueConditional;
        var entity = this.getEntity(executionOptions);
        if (entity) {
            var collectionItem = this.getCollectionItem(executionOptions);
            var _b = (collectionItem || {}).values, values = _b === void 0 ? [] : _b;
            var matchingValues = values.filter(function (value) { return !collectionItemValueConditional || conditionalHelper.matches(value, collectionItemValueConditional); });
            if (matchingValues && matchingValues.length > 0) {
                var item = this.getItem(executionOptions, matchingValues);
                if (item) {
                    if (key) {
                        return item[key];
                    }
                    return item;
                }
            }
        }
        return;
    };
    BaseQuery.prototype.getRawValueForContentElement = function (executionOptions, contentItemKey) {
        var _a = this.options, queryOptionsType = _a.queryOptionsType, key = _a.key;
        if (this.options.entityType) {
            var entity = this.getEntity(executionOptions);
            if (entity) {
                var contentObject = entity[queryOptionsType];
                if (contentObject) {
                    var contentTypeCollection = contentObject[key];
                    if (contentTypeCollection) {
                        var culture_1 = executionOptions.culture, fallbackCulture_1 = executionOptions.fallbackCulture;
                        var contentItem = contentTypeCollection.find(function (item) { return item.culture === culture_1; });
                        if (!contentItem) {
                            contentItem = contentTypeCollection.find(function (item) { return item.culture === fallbackCulture_1; });
                        }
                        return (contentItem && contentItem[contentItemKey]);
                    }
                }
            }
        }
        else {
            var keysByContentItemKey = {
                'commentary': {
                    globalContextKey: 'globalCommentaries',
                    contentParentKey: 'commentaryType',
                    contentValuesKey: 'commentaries',
                },
                'disclaimer': {
                    globalContextKey: 'globalDisclaimers',
                    contentParentKey: 'disclaimerType',
                    contentValuesKey: 'disclaimers',
                },
            };
            var keysForContentItemKey = keysByContentItemKey[contentItemKey];
            if (keysForContentItemKey) {
                var globalContextKey = keysForContentItemKey.globalContextKey, contentParentKey_1 = keysForContentItemKey.contentParentKey, contentValuesKey = keysForContentItemKey.contentValuesKey;
                var context = Query_1.Query.getContext(executionOptions, this.options);
                if (context) {
                    var collection = context[globalContextKey];
                    if (collection && Array.isArray(collection)) {
                        var contentParent = collection.find(function (item) { return item[contentParentKey_1] === key; });
                        if (contentParent) {
                            var contentTypeCollection = contentParent[contentValuesKey];
                            if (contentTypeCollection) {
                                var culture_2 = executionOptions.culture, fallbackCulture_2 = executionOptions.fallbackCulture;
                                var contentItem = contentTypeCollection.find(function (item) { return item.culture === culture_2; });
                                if (!contentItem) {
                                    contentItem = contentTypeCollection.find(function (item) { return item.culture === fallbackCulture_2; });
                                }
                                return (contentItem && contentItem[contentItemKey]);
                            }
                        }
                    }
                }
            }
        }
    };
    return BaseQuery;
}());
exports.BaseQuery = BaseQuery;
//# sourceMappingURL=BaseQuery.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@kurtosys/ksys-app-template/dist/query/BaseQuery.js
// module id = 14
// module chunks = 1